import { useEffect } from 'react'
import { useForm, Controller, useFieldArray } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, TextField, MenuItem,
  Alert, CircularProgress, IconButton, Typography, Divider,
} from '@mui/material'
import { Add, DeleteOutlined } from '@mui/icons-material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createFlow, updateFlow, getFlow, listFlowDocumentTypes, listFlowRoles,
  type FlowDefinitionListItem, type SaveFlowDefinitionPayload,
} from '../../api/aprobari'
import { errorMessage } from '../../api/client'

const stepSchema = z.object({
  id: z.string().nullable(),
  name: z.string().min(1, 'Câmp obligatoriu').max(128),
  approverRoleId: z.string().min(1, 'Selectați un rol'),
})

const schema = z.object({
  documentType: z.string().min(1, 'Selectați tipul de document'),
  name: z.string().min(1, 'Câmp obligatoriu').max(128),
  minAmount: z.string().optional(),
  currency: z.string().optional(),
  steps: z.array(stepSchema).min(1, 'Adăugați cel puțin un pas'),
})
type FormValues = z.infer<typeof schema>

const EMPTY: FormValues = { documentType: '', name: '', minAmount: '', currency: '', steps: [] }

function numOrNull(v: string | undefined): number | null {
  if (!v) return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

export default function FlowFormDialog({ flow, onClose }: { flow: FlowDefinitionListItem | null | 'new'; onClose: () => void }) {
  const open = flow !== null
  const editing = flow !== null && flow !== 'new'
  const qc = useQueryClient()

  const { data: documentTypes } = useQuery({ queryKey: ['administrare', 'fluxuri-aprobare', 'tipuri-document'], queryFn: listFlowDocumentTypes, enabled: open })
  const { data: roles } = useQuery({ queryKey: ['administrare', 'fluxuri-aprobare', 'roluri'], queryFn: listFlowRoles, enabled: open })
  const { data: detail } = useQuery({
    queryKey: ['administrare', 'fluxuri-aprobare', editing ? (flow as FlowDefinitionListItem).id : null],
    queryFn: () => getFlow((flow as FlowDefinitionListItem).id),
    enabled: editing,
  })

  const { control, handleSubmit, reset, formState: { errors } } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: EMPTY })
  const steps = useFieldArray({ control, name: 'steps' })

  useEffect(() => {
    if (editing && detail) {
      reset({
        documentType: detail.documentType, name: detail.name,
        minAmount: detail.minAmount != null ? String(detail.minAmount) : '', currency: detail.currency ?? '',
        steps: detail.steps.map((s) => ({ id: s.id, name: s.name, approverRoleId: s.approverRoleId })),
      })
    } else if (flow === 'new') {
      reset(EMPTY)
    }
  }, [flow, editing, detail, reset])

  const mutation = useMutation({
    mutationFn: async (values: FormValues) => {
      const payload: SaveFlowDefinitionPayload = {
        documentType: values.documentType, name: values.name,
        minAmount: numOrNull(values.minAmount), currency: values.currency || null,
        steps: values.steps.map((s, i) => ({ id: s.id, stepOrder: i + 1, name: s.name, approverRoleId: s.approverRoleId })),
      }
      if (editing) return updateFlow((flow as FlowDefinitionListItem).id, payload)
      return createFlow(payload)
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['administrare', 'fluxuri-aprobare'] })
      onClose()
    },
  })

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit((v) => mutation.mutate(v))}>
        <DialogTitle>{editing ? 'Editare flux de aprobare' : 'Flux de aprobare nou'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <Controller name="documentType" control={control} render={({ field }) => (
              <TextField {...field} select label="Tip document" fullWidth error={!!errors.documentType} helperText={errors.documentType?.message}>
                {documentTypes?.map((d) => <MenuItem key={d.code} value={d.code}>{d.name}</MenuItem>)}
              </TextField>
            )} />
            <Controller name="name" control={control} render={({ field }) => (
              <TextField {...field} label="Denumire flux" fullWidth error={!!errors.name} helperText={errors.name?.message} />
            )} />
            <Stack direction="row" spacing={2}>
              <Controller name="minAmount" control={control} render={({ field }) => (
                <TextField {...field} type="number" label="Prag valoric minim (opțional)" fullWidth />
              )} />
              <Controller name="currency" control={control} render={({ field }) => (
                <TextField {...field} label="Monedă" fullWidth sx={{ maxWidth: 140 }} />
              )} />
            </Stack>

            <Divider textAlign="left">
              <Typography variant="caption" color="text.secondary">Pași de aprobare</Typography>
            </Divider>
            {typeof errors.steps?.message === 'string' && <Alert severity="warning">{errors.steps.message}</Alert>}

            {steps.fields.map((f, index) => (
              <Stack key={f.id} direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
                <Typography variant="body2" sx={{ pt: 1.5, minWidth: 20 }}>{index + 1}.</Typography>
                <Controller name={`steps.${index}.name`} control={control} render={({ field }) => (
                  <TextField {...field} label="Denumire pas" fullWidth
                    error={!!errors.steps?.[index]?.name} helperText={errors.steps?.[index]?.name?.message} />
                )} />
                <Controller name={`steps.${index}.approverRoleId`} control={control} render={({ field }) => (
                  <TextField {...field} select label="Aprobator (rol)" fullWidth sx={{ minWidth: 200 }}
                    error={!!errors.steps?.[index]?.approverRoleId} helperText={errors.steps?.[index]?.approverRoleId?.message}>
                    {roles?.map((r) => <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>)}
                  </TextField>
                )} />
                <IconButton onClick={() => steps.remove(index)} sx={{ mt: 0.5 }}><DeleteOutlined fontSize="small" /></IconButton>
              </Stack>
            ))}
            <Button startIcon={<Add />} onClick={() => steps.append({ id: null, name: '', approverRoleId: '' })} sx={{ alignSelf: 'flex-start' }}>
              Adaugă pas
            </Button>

            {Object.keys(errors).length > 0 && (
              <Alert severity="warning">Există câmpuri obligatorii necompletate — verificați câmpurile marcate cu roșu mai sus.</Alert>
            )}
            {mutation.isError && <Alert severity="error">{errorMessage(mutation.error)}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={onClose}>Renunță</Button>
          <Button type="submit" variant="contained" disabled={mutation.isPending}
            startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}>
            Salvează
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
